/**
 * To debug re-renders with why-did-you-render library, uncomment this import
 * and disable Prettier vscode extension (this import should be first of all)
 */
// import 'lib/whyDidYouRender';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';

import GlobalLayout from 'components/Layout/Global';
import GlobalScripts from 'components/Scripts/GlobalScripts';

import { sendWebVitalsToGoogleAnalytics } from 'lib/gtag';

import GlobalProvider from 'providers/GlobalProvider';

import APP from 'constants/app';

import 'style/globals.css';

const WEB_VITAL_METRIC_LABEL = 'web-vital';

export function reportWebVitals(metric) {
  if (metric.label === WEB_VITAL_METRIC_LABEL) {
    sendWebVitalsToGoogleAnalytics(metric);
  }
}

// Stores the href of the offer card nearest to the viewport center
// when navigating away. Used to refine scroll position after back navigation
// since pixel-based restoration is inaccurate due to lazy-loaded images
// changing the document height.
let savedAnchorHref = null;

const PromobitApp = (props) => {
  const { Component, pageProps } = props || {};
  const router = useRouter();
  const correctionTimerRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const onRouteChangeStart = () => {
      // Save the offer card closest to viewport center
      const viewportMiddle = window.scrollY + window.innerHeight / 2;
      const cards = document.querySelectorAll('a[href*="/oferta/"]');
      let closest = null;
      let closestDist = Infinity;

      cards.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const elMiddle = window.scrollY + rect.top + rect.height / 2;
        const dist = Math.abs(elMiddle - viewportMiddle);
        if (dist < closestDist) {
          closestDist = dist;
          closest = el;
        }
      });

      savedAnchorHref = closest ? closest.getAttribute('href') : null;
    };

    const onPopState = () => {
      if (!savedAnchorHref) {
        return;
      }

      const href = savedAnchorHref;

      // Wait for Next.js scroll restoration to happen first, then correct
      let attempts = 0;
      const correct = () => {
        const el = document.querySelector(`a[href="${href}"]`);
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'instant' });
          savedAnchorHref = null;
          correctionTimerRef.current = null;
        } else if (attempts < 50) {
          attempts += 1;
          correctionTimerRef.current = setTimeout(correct, 100);
        } else {
          savedAnchorHref = null;
          correctionTimerRef.current = null;
        }
      };

      // Delay to let Next.js do its pixel-based scroll first (which triggers
      // infinite scroll loading), then correct to the exact element
      correctionTimerRef.current = setTimeout(correct, 500);
    };

    router.events.on('routeChangeStart', onRouteChangeStart);
    window.addEventListener('popstate', onPopState);

    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart);
      window.removeEventListener('popstate', onPopState);
      if (correctionTimerRef.current) {
        clearTimeout(correctionTimerRef.current);
      }
    };
  }, [router]);

  if (!Component) {
    return null;
  }

  return (
    <GlobalProvider {...(pageProps?.[APP.CTX_OPTIONAL_PROPS] || {})}>
      <Script
        crossOrigin="anonymous"
        id="adsbygoogle-script"
        src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-9700650747779397"
        strategy="afterInteractive"
      />
      <GlobalScripts />
      <GlobalLayout
        isRecoveryPasswordValid={
          !!pageProps?.recoveryPassword && pageProps.recoveryPassword?.isValid
        }
      >
        <Head>
          {/*
            Customize `viewport` meta-tag to avoid auto zoom in input when using
            iPhone devices
          */}
          <meta
            content="minimum-scale=1, width=device-width, initial-scale=1.0, maximum-scale=1.0, viewport-fit=cover, shrink-to-fit=no"
            name="viewport"
          />
        </Head>
        <Component {...pageProps} />
      </GlobalLayout>
    </GlobalProvider>
  );
};

PromobitApp.propTypes = {
  pageProps: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    categories: PropTypes.array,
    // eslint-disable-next-line react/forbid-prop-types
    stores: PropTypes.array,
    topbarAd: PropTypes.shape({
      imageSrc: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      isClosable: PropTypes.bool.isRequired,
      bgColor: PropTypes.string.isRequired,
    }),
  }),
};

export default PromobitApp;
